import React, { useState, useEffect, useRef } from 'react';
import { PinnedDetail, useScrollViewport } from './PinnedDetail';
import { Card, Table, RNLLoader, Modal, Select, Btn, Input, useToast, ReturnDestinationNote, StatusReason } from '../UI';
import * as api from '../../api';
import { offersConsiderationStatus, isAwaitingCorrection, correctionTarget, considerationOptionsFor, defaultConsiderationFor } from '../../utils';

import PRDetailsView from './PRDetailsView';
import NewRequisition from './NewRequisition';
import RequisitionExpandedDetail from './RequisitionExpandedDetail';

// An item is in someone's queue for one of two reasons: it reached them going
// forward, or a LATER step returned it to them for correction. The STEP status
// is 'pending' in both cases — which is why corrections used to file under the
// Pending tab wearing a PENDING badge.
//
// Whether it is worth FETCHING the consideration statuses at all. Whether the
// picker actually shows is decided by the destination — see
// offersConsiderationStatus — because the status only means anything if the
// return lands at Cost Control. This is the cheap pre-filter: a department
// head can only return to the requester, so there is nothing to fetch.
const usesConsiderationStatus = (item) => {
  const step = (item?.step_name || '').toLowerCase();
  return step.includes('cost control') || step.includes('approval queue');
};

// Terminal outcomes have to come from the REQUEST, not the step. When Cost
// Control rejects, the step in this queue reads 'approved' — the manager did
// approve it — so a step-only read filed the row under Pending and left the
// Rejected tab on 0 forever. Anything not returned/rejected still shows the
// step's own state, so healthy rows keep their PENDING badge rather than being
// relabelled with the request-level IN_PROGRESS.
// Shown on the confirm button while the action is in flight.
const ACTION_PROGRESS = {
  approve: 'Approving…',
  reject: 'Rejecting…',
  return: 'Returning…',
  hold: 'Placing on hold…',
  delete: 'Deleting…',
};

const displayStatus = (item) => {
  if (isAwaitingCorrection(item)) return 'RETURNED';
  const outcome = (item?.status_view?.code || item?.context_data?.request_status || '').toUpperCase();
  if (outcome === 'REJECTED' || outcome === 'RETURNED') return outcome;
  return (item?.status || '').toUpperCase();
};

export default function MyInbox({ stepNameFilter, title, departmentId, onEditingChange, onUnseenCountChange } = {}) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  // Caps the expanded requisition at the visible width so its line items
  // scroll on their own rather than dragging the panel sideways.
  const viewport = useScrollViewport([loading]);
  const [inboxItems, setInboxItems] = useState([]);
  const [activeTab, setActiveTab] = useState('PENDING'); // PENDING, RETURNED, REJECTED
  const [currentUserId, setCurrentUserId] = useState(null);
  // Per-user "seen" tracking stored in localStorage so the badge count drops
  // when this user acknowledges an item and stays gone across page refreshes.
  // Keyed by user_id so different accounts on the same browser are independent.
  const getSeenKey = (uid) => `inbox_seen_${uid}`;
  const loadSeenIds = (uid) => {
    if (!uid) return new Set();
    try { return new Set(JSON.parse(localStorage.getItem(getSeenKey(uid)) || '[]')); }
    catch { return new Set(); }
  };
  const saveSeenIds = (uid, set) => {
    if (!uid) return;
    try { localStorage.setItem(getSeenKey(uid), JSON.stringify([...set])); } catch {}
  };

  // Counts derived from inbox items — no server poll needed.
  const [unreadCounts, setUnreadCounts] = useState({ returned: 0, rejected: 0 });
  // True when the viewer holds the department_head role AND belongs to this
  // department — e.g. an assistant department head who isn't the one specific
  // person the step is assigned to, but should still be able to act on their behalf.
  const [isDepartmentHeadPeer, setIsDepartmentHeadPeer] = useState(false);

  // Action Modals State
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [outcomeModal, setOutcomeModal] = useState(null);
  // Synchronous double-submit guard — see handleActionSubmit.
  const actingRef = useRef(false);

  // View State
  const [viewingPrId, setViewingPrId] = useState(null);
  const [viewingInstanceId, setViewingInstanceId] = useState(null);
  const [viewingReadOnly, setViewingReadOnly] = useState(false);
  const [editingPrId, setEditingPrId] = useState(null);
  // Correcting a request takes over the screen, so the parent drops its
  // department-hub chrome. Without this the editor rendered beneath a "Back to
  // Department Hub" bar and a tab row belonging to the page that launched it,
  // giving two competing ways out.
  useEffect(() => {
    if (onEditingChange) onEditingChange(!!editingPrId);
  }, [editingPrId, onEditingChange]);

  // Once we know who is logged in, hydrate seenEntityIds from their localStorage.
  useEffect(() => {
    if (currentUserId) {
      setSeenEntityIds(loadSeenIds(currentUserId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // Expanded rows. Inbox items carry only ids + context_data, so the full
  // requisition is fetched on first expand and cached — collapsing and
  // re-expanding doesn't re-hit the API.
  const [expandedRows, setExpandedRows] = useState({});
  const [prCache, setPrCache] = useState({});
  const [prLoading, setPrLoading] = useState({});
  // Guards the Approve/Return/Reject confirm button while the action is in flight.
  const [acting, setActing] = useState(false);

  const toggleExpanded = async (row) => {
    const id = row.entity_id;
    const opening = !expandedRows[id];
    setExpandedRows(prev => ({ ...prev, [id]: opening }));


    if (!opening || prCache[id] || prLoading[id]) return;
    setPrLoading(prev => ({ ...prev, [id]: true }));
    try {
      const res = await api.getPR(id);
      setPrCache(prev => ({ ...prev, [id]: res?.data || res }));
    } catch (e) {
      console.error('Failed to load requisition detail', e);
    } finally {
      setPrLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  // Form State
  const [comment, setComment] = useState('');
  // Where a return will land, and the reason it is being parked. Both fetched
  // per instance so the options always match what the server accepts.
  const [returnTargets, setReturnTargets] = useState([]);
  const [returnTarget, setReturnTarget] = useState('');
  // The step doing the returning — decides whether a consideration
  // status is on offer at all. See offersConsiderationStatus.
  const [returnFromStep, setReturnFromStep] = useState('');
  const [considerationStatuses, setConsiderationStatuses] = useState([]);
  const [considerationStatusId, setConsiderationStatusId] = useState('');
  const [nextApprovers, setNextApprovers] = useState([]);
  const [selectedNextApprover, setSelectedNextApprover] = useState('');
  const [isFetchingApprovers, setIsFetchingApprovers] = useState(false);

  // Recompute badge counts from live inbox items and localStorage seen set.
  const refreshCounts = (items, userId) => {
    const seen = loadSeenIds(userId || currentUserId);
    const filtered = stepNameFilter
      ? items.filter(i => i.entity_type === 'purchase_request' && (i.step_name || '').toLowerCase().includes(stepNameFilter.toLowerCase()))
      : items;
    const isRet = (i) => { const s = displayStatus(i); return s === 'RETURNED' || s.includes('RETURN'); };
    const unseenReturned = filtered.filter(i => isRet(i) && !seen.has(i.entity_id)).length;
    const unseenRejected = filtered.filter(i => displayStatus(i) === 'REJECTED' && !seen.has(i.entity_id)).length;
    const pendingCount = filtered.filter(i => !isRet(i) && displayStatus(i) !== 'REJECTED').length;
    setUnreadCounts({ returned: unseenReturned, rejected: unseenRejected });
    if (onUnseenCountChange) onUnseenCountChange(pendingCount + unseenReturned + unseenRejected);
  };

  const fetchInbox = async () => {
    setLoading(true);
    try {
      api.clearApiCache();
      // getMe() is fetched in BOTH modes. It used to be department-mode only,
      // which left currentUserId null everywhere else and forced the action
      // gate below to fall back to "allow" — see canAct.
      const [res, meRes] = await Promise.all([
        departmentId ? api.getDepartmentInbox(departmentId, stepNameFilter) : api.getMyInbox(),
        api.getMe().catch(() => null),
      ]);
      const items = Array.isArray(res) ? res : (res?.data || []);
      setInboxItems(items);
      const me = meRes?.data || meRes;
      const uid = me?.user_id || me?.id || null;
      setCurrentUserId(uid);
      const meRoleNames = (me?.roles || []).map((r) => (typeof r === 'string' ? r : (r.role_name || r.name || '')).toLowerCase());
      const meDepartmentIds = (me?.departments || []).map((d) => d.id || d.department_id);
      setIsDepartmentHeadPeer(
        !!departmentId && meRoleNames.includes('department_head') && meDepartmentIds.includes(departmentId)
      );
      refreshCounts(items, uid);
    } catch (e) {
      toast.show('Failed to load inbox.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Tracks entity ids the user has explicitly marked seen — persisted in
  // localStorage so the green button and zero badge survive a page refresh.
  const [seenEntityIds, setSeenEntityIds] = useState(new Set());

  const handleMarkSeen = (entityId) => {
    const next = new Set([...seenEntityIds, entityId]);
    setSeenEntityIds(next);
    saveSeenIds(currentUserId, next);
    refreshCounts(inboxItems, currentUserId);
  };

  useEffect(() => {
    fetchInbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, stepNameFilter]);

  const [nextStepInfo, setNextStepInfo] = useState(null);
  // `${instance_id}:${type}` for the row whose action is being prepared, so
  // only the button that was pressed shows a spinner.
  const [preparingAction, setPreparingAction] = useState(null);

  const openActionModal = async (item, type) => {
    setSelectedInstance(item);
    setActionType(type);
    setComment('');
    setReturnTarget(''); setReturnTargets([]);
    setConsiderationStatusId('');
    setSelectedNextApprover('');
    setNextStepInfo(null);

    // The next step is fetched BEFORE the modal appears, not after. Opening
    // first meant the dialog popped with a "Loading next step..." placeholder
    // that was replaced a moment later, resizing the modal under the pointer.
    // The wait now shows on the button the user just pressed, which is where
    // they are already looking.
    if (type === 'approve') {
      setPreparingAction(`${item.instance_id}:${type}`);
      setIsFetchingApprovers(true);
      try {
        const details = await api.getNextStepDetails(item.instance_id);
        const data = details?.data || details;
        setNextStepInfo(data);
        if (data?.approver_mode === 'specific_user' && data?.eligible_users?.length > 0) {
          setSelectedNextApprover(data.eligible_users[0].user_id || data.eligible_users[0].id);
        }
      } catch (e) {
        // Non-fatal: the modal still opens and the approve can proceed — the
        // step selector just falls back to its own empty state.
        console.error("Failed to fetch next step details", e);
      } finally {
        setIsFetchingApprovers(false);
        setPreparingAction(null);
      }
    }

    setShowActionModal(true);
  };

  // Loaded when the return dialog opens: both depend on where the request
  // currently sits, which can change while this page is open.
  useEffect(() => {
    if (!showActionModal || actionType !== 'return' || !selectedInstance?.instance_id) return;
    let cancelled = false;
    api.getReturnTargets(selectedInstance.instance_id)
      .then((res) => {
        if (cancelled) return;
        setReturnTargets(res?.targets || []);
        setReturnFromStep(res?.current_step_name || '');
        setReturnTarget(res?.default_target || 'requester');
      })
      // Non-fatal: with no list the picker hides and the server falls back to
      // the step's configured behaviour.
      .catch(() => { if (!cancelled) { setReturnTargets([]); setReturnTarget(''); } });

    if (usesConsiderationStatus(selectedInstance)) {
      api.listConsiderationStatuses({ activeOnly: true, returnReasonsOnly: true })
        .then((res) => {
          if (cancelled) return;
          setConsiderationStatuses(Array.isArray(res) ? res : (res.consideration_statuses || []));
        })
        .catch(() => { });
    } else {
      setConsiderationStatuses([]);
    }
    return () => { cancelled = true; };
  }, [showActionModal, actionType, selectedInstance]);

  // Clearing this when the destination stops being Cost Control: the picker
  // hides, but without this the previously chosen value would still be sent
  // and would park the request under a heading nobody will look at.
  useEffect(() => {
    if (!offersConsiderationStatus(returnFromStep, returnTargets, returnTarget)) {
      setConsiderationStatusId('');
      return;
    }
    // The destination pre-selects a matching heading; still changeable after.
    const label = (returnTargets || []).find((t) => t.value === returnTarget)?.label;
    const suggested = defaultConsiderationFor(label, considerationStatuses);
    if (suggested) setConsiderationStatusId(suggested);
  }, [returnTarget, returnTargets, returnFromStep, considerationStatuses]);

  const handleActionSubmit = async () => {
    // A ref, not the `acting` state: setActing is async, so two clicks in the
    // same tick both get past a state check before React re-renders and
    // disables the button, firing the same workflow action twice.
    if (actingRef.current) return;
    if (acting) return;
    actingRef.current = true;
    if (actionType === 'delete') {
      setActing(true);
      try {
        await api.deletePR(selectedInstance.entity_id);
        setShowActionModal(false);
        setOutcomeModal({
          type: 'success',
          title: 'Request Deleted Successfully!',
          message: 'The purchase request has been permanently deleted.'
        });
        fetchInbox();
      } catch (e) {
        setShowActionModal(false);
        setOutcomeModal({
          type: 'error',
          title: 'Action Failed',
          message: e.response?.data?.detail || e.message || 'Failed to delete request'
        });
      } finally {
        actingRef.current = false;
        setActing(false);
      }
      return;
    }

    // The heading is what files the return in Pending Consideration, so it is
    // required whenever the picker is on screen.
    if (actionType === 'return'
        && offersConsiderationStatus(returnFromStep, returnTargets, returnTarget)
        && !considerationStatusId) {
      toast.show('Choose a consideration status for this return.', 'error');
      actingRef.current = false;
      return;
    }

    if ((actionType === 'reject' || actionType === 'return') && !comment.trim()) {
      toast.show(`Comment is required for ${actionType}.`, 'error');
      actingRef.current = false;
      return;
    }
    
    if (actionType === 'approve' && nextStepInfo?.approver_mode === 'specific_user' && !selectedNextApprover) {
      toast.show('Please select the specific approver for this step.', 'error');
      actingRef.current = false;
      return;
    }

    const wasCorrection = isAwaitingCorrection(selectedInstance);
    const correctionTargetName = correctionTarget(selectedInstance);

    setActing(true);
    try {
      await api.actOnWorkflow(selectedInstance.instance_id, {
        action: actionType,
        comment: comment,
        next_approver_id: nextStepInfo?.approver_mode === 'specific_user' ? selectedNextApprover : undefined,
        // Both only meaningful on a return; the server ignores them otherwise.
        return_target: actionType === 'return' && returnTarget ? returnTarget : undefined,
        consideration_status_id:
          actionType === 'return' && considerationStatusId ? considerationStatusId : undefined,
      });
      setShowActionModal(false);
      setOutcomeModal({
        type: actionType,
        // Approving a correction doesn't advance the chain — it sends the
        // request back to the step that asked for the change.
        title: actionType === 'approve'
          ? (wasCorrection ? 'Request Resubmitted!' : 'Request Approved Successfully!')
          : (actionType === 'return' ? 'Request Returned!' : 'Request Rejected!'),
        message: actionType === 'approve'
          ? (wasCorrection
              ? `The corrected request has been sent back to ${correctionTargetName}.`
              : 'The purchase request has been officially approved and forwarded to the next step in the workflow.')
          : (actionType === 'return' ? 'The purchase request has been returned to the requester.' : 'The purchase request has been rejected and returned to the requester.')
      });
    } catch (e) {
      setShowActionModal(false);
      setOutcomeModal({
        type: 'error',
        title: 'Action Failed',
        message: e.response?.data?.detail || e.message || `Failed to ${actionType} request`
      });
    } finally {
      actingRef.current = false;
      setActing(false);
    }
  };

  if (loading) return <RNLLoader title="Loading Inbox..." subtitle="Fetching pending requests" />;

  if (viewingPrId && viewingInstanceId) {
    return (
      <PRDetailsView
        prId={viewingPrId}
        instanceId={viewingInstanceId}
        viewContext={viewingReadOnly ? 'readonly' : 'generic'}
        onEdit={(id) => {
          setViewingPrId(null);
          setEditingPrId(id);
        }}
        onBack={() => {
          setViewingPrId(null);
          setViewingReadOnly(false);
          fetchInbox(); // Refresh inbox when going back
        }}
      />
    );
  }

  if (editingPrId) {
    return (
      <NewRequisition
        editPrId={editingPrId}
        onSaveSuccess={() => {
          setEditingPrId(null);
          fetchInbox();
        }}
      />
    );
  }

  const tableCols = [
    { key: 'entity_id', label: 'REFERENCE ID', align: 'center', render: (_, row) => (
      <span
        onClick={() => toggleExpanded(row)}
        style={{ fontWeight: 600, color: '#0b318bff', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      >
        <span style={{ display: 'inline-block', width: 12, marginRight: 6, color: '#64748b' }}>
          {expandedRows[row.entity_id] ? '▼' : '▶'}
        </span>
        {row.context_data?.pr_number || 'UNKNOWN'}
      </span>
    ) },
    { key: 'entity_type', label: 'TYPE', align: 'center', render: (v) => v === 'purchase_request' ? 'Purchase Request' : v },
    { key: 'initiator', label: 'INITIATOR', align: 'center', render: (_, row) => row.initiator_name || '—' },
    { key: 'pending_since', label: 'ASSIGNED ON', align: 'center', render: (v) => v ? v.split('T')[0] : '—' },
    { key: 'step_name', label: 'CURRENT STEP', align: 'center' },
    { 
      key: 'status', 
      label: 'STATUS', 
      align: 'center', 
      render: (_, row) => {
        const vUpper = displayStatus(row);
        const isApproved = vUpper === 'APPROVED';
        const isReturned = isAwaitingCorrection(row) || vUpper === 'RETURNED';
        const isPending = !isReturned && (vUpper === 'PENDING' || vUpper.includes('REVIEW') || vUpper === 'ON_HOLD');
        
        return (
          <>
          <span style={{
            padding: '4px 10px',
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 600,
            background: isApproved ? '#dcfce7' : isReturned ? '#ffedd5' : isPending ? '#fef3c7' : '#fee2e2',
            color: isApproved ? '#16a34a' : isReturned ? '#ea580c' : isPending ? '#d97706' : '#dc2626',
            border: `1px solid ${isApproved ? '#86efac' : isReturned ? '#fdba74' : isPending ? '#fcd34d' : '#fca5a5'}`,
            whiteSpace: 'nowrap'
          }}>
            {vUpper.replace(/_/g, ' ')}
          </span>
          <StatusReason reason={row.status_reason} />
          </>
        );
      }
    },
    { key: 'actions', label: 'ACTIONS', align: 'center', render: (_, row) => {
      // Every member of a department can see the queue, but only the person the
      // step is assigned to gets Edit/Review — the backend rejects
      // act-on-workflow from anyone else anyway, so this keeps the UI honest.
      //
      // Fails CLOSED: you may act only if the step is actually assigned to
      // you, or you're a department_head standing in for the assigned head.
      // This used to start with `!departmentId ||`, so any view that didn't
      // pass a department — or hadn't resolved one yet on first render — handed
      // every viewer the full Edit/Delete/Return/Authorize set on requests
      // assigned to someone else. Everyone else gets the read-only eye.
      const canAct = !!(currentUserId && row.assigned_user_id === currentUserId) || isDepartmentHeadPeer;

      // A rejected request is terminal — nothing left to authorize, return,
      // edit or delete. It stays visible as a record, so it gets the eye and
      // nothing else, whoever is looking.
      const isRejected = displayStatus(row) === 'REJECTED';

      // This item is in the holder's queue because a LATER step returned the
      // request to them for correction, not because it reached them going
      // forward. Approving it doesn't advance the chain — it sends the
      // corrected request back to the step that asked for the change — so the
      // button is labelled for what it actually does.
      const sentBackForCorrection = isAwaitingCorrection(row);
      const targetStepName = correctionTarget(row);
      if (!canAct || isRejected) {
        const s = displayStatus(row);
        const isReturnedHere = s === 'RETURNED' || s.includes('RETURN') || isAwaitingCorrection(row);
        const showAck = isRejected || isReturnedHere;
        const alreadySeen = seenEntityIds.has(row.entity_id);
        return (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
            <button
              onClick={() => {
                setViewingReadOnly(true);
                setViewingPrId(row.entity_id);
                setViewingInstanceId(row.instance_id);
              }}
              title="View Request"
              style={{ background: 'transparent', color: '#0b318bff', border: 'none', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
            </button>
            {showAck && (
              <button
                onClick={(e) => { e.stopPropagation(); if (!alreadySeen) handleMarkSeen(row.entity_id); }}
                title={alreadySeen ? 'Seen' : 'Mark as seen'}
                style={{
                  background: alreadySeen ? '#dcfce7' : 'transparent',
                  color: alreadySeen ? '#16a34a' : '#6b7280',
                  border: `1px solid ${alreadySeen ? '#86efac' : '#d1d5db'}`,
                  borderRadius: 6, padding: '4px',
                  cursor: alreadySeen ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="9 12 11 14 15 10"></polyline></svg>
              </button>
            )}
          </div>
        );
      }
      // Any action in flight locks the whole row. Preparing an approve opens
      // a modal a moment later, so leaving View / Edit / Delete / Return live
      // let a second click land in the gap and either navigate away from the
      // modal that was about to appear, or start a competing action.
      const busy = !!preparingAction;
      const iconBtn = (color) => ({
        background: 'transparent', color, border: 'none', padding: '4px',
        cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.4 : 1,
        display: 'flex', alignItems: 'center',
      });

      const isReturnedActive = isAwaitingCorrection(row) || displayStatus(row) === 'RETURNED' || displayStatus(row).includes('RETURN');
      const alreadySeenActive = seenEntityIds.has(row.entity_id);
      return (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
          {/* Eye - View */}
          <button
            onClick={() => {
              setViewingReadOnly(false);
              setViewingPrId(row.entity_id);
              setViewingInstanceId(row.instance_id);
            }}
            title="View Request"
            disabled={busy}
            style={iconBtn('#0b318bff')}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
          </button>

          {/* Mark seen — shown for returned rows; turns green after clicking */}
          {isReturnedActive && (
            <button
              onClick={(e) => { e.stopPropagation(); if (!alreadySeenActive) handleMarkSeen(row.entity_id); }}
              title={alreadySeenActive ? 'Seen' : 'Mark as seen'}
              disabled={busy}
              style={{
                background: alreadySeenActive ? '#dcfce7' : 'transparent',
                color: alreadySeenActive ? '#16a34a' : '#6b7280',
                border: `1px solid ${alreadySeenActive ? '#86efac' : '#d1d5db'}`,
                borderRadius: 6, padding: '4px',
                cursor: busy ? 'not-allowed' : (alreadySeenActive ? 'default' : 'pointer'),
                opacity: busy ? 0.4 : 1,
                display: 'flex', alignItems: 'center',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="9 12 11 14 15 10"></polyline></svg>
            </button>
          )}

          {/* Pencil - Edit */}
          {row.entity_type === 'purchase_request' && (
            <button
              onClick={() => setEditingPrId(row.entity_id)}
              title={sentBackForCorrection
                ? `Correct this request, then send it back to ${targetStepName}`
                : 'Edit this request before deciding'}
              disabled={busy}
              style={iconBtn('#eab308')}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
            </button>
          )}

          {/* Trash - Delete. Not offered on a request returned here for
              correction: it belongs to the requester and is mid-approval, so
              the job is to fix it and send it on, not destroy it. */}
          {!sentBackForCorrection && (
          <button
            onClick={() => openActionModal(row, 'delete')}
            title="Delete Request"
            disabled={busy}
            style={iconBtn('#ef4444')}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
          </button>
          )}

          {/* Return. Already returned once and sitting here for correction —
              bouncing it back again would ping-pong it between the same two
              approvers. */}
          {!sentBackForCorrection && (
          <button
            onClick={() => openActionModal(row, 'return')}
            title="Return Request"
            disabled={busy}
            style={{ background: '#fff', color: '#ea580c', border: '1px solid #ea580c', padding: '6px 12px', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.4 : 1, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 14 4 9 9 4"></polyline><path d="M20 20v-7a4 4 0 0 0-4-4H4"></path></svg>
            Return
          </button>
          )}

          {/* Authorize — or, for a request sent back for correction, Resubmit */}
          {(() => {
            // `preparing` is this row's own approve (spinner + label);
            // `busy` covers any action anywhere, so a second approve cannot be
            // started from another row while one is being prepared.
            const preparing = preparingAction === `${row.instance_id}:approve`;
            return (
              <button
                onClick={() => openActionModal(row, 'approve')}
                disabled={busy}
                title={sentBackForCorrection
                  ? `Send the corrected request back to ${targetStepName}`
                  : 'Authorize Request'}
                style={{ background: sentBackForCorrection ? '#0b318bff' : '#10b981', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: busy ? (preparing ? 'wait' : 'not-allowed') : 'pointer', opacity: preparing ? 0.75 : (busy ? 0.4 : 1), fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
              >
                {preparing ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.7s linear infinite' }}>
                    <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.35)" strokeWidth="2.6" />
                    <path d="M12 3a9 9 0 0 1 9 9" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" />
                  </svg>
                ) : sentBackForCorrection ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 10 20 15 15 20"></polyline><path d="M4 4v7a4 4 0 0 0 4 4h12"></path></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 16 16 12 12 8"></polyline><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                )}
                {preparing
                  ? 'Authorizing…'
                  : (sentBackForCorrection ? `Resubmit to ${targetStepName}` : 'Authorize')}
              </button>
            );
          })()}
        </div>
      );
    }}
  ];

  const baseItems = stepNameFilter
    ? inboxItems.filter((item) =>
        item.entity_type === 'purchase_request' &&
        (item.step_name || '').toLowerCase().includes(stepNameFilter.toLowerCase())
      )
    : inboxItems;

  const isReturnedItem = (item) => {
    const s = displayStatus(item);
    return s === 'RETURNED' || s.includes('RETURN');
  };

  const returnedItems = baseItems.filter(isReturnedItem);

  const rejectedItems = baseItems.filter(item => displayStatus(item) === 'REJECTED');

  const pendingItems = baseItems.filter(
    item => !isReturnedItem(item) && displayStatus(item) !== 'REJECTED'
  );

  const displayItems = activeTab === 'PENDING' ? pendingItems : activeTab === 'RETURNED' ? returnedItems : rejectedItems;

  // The shared Table renders `_isGroupHeader` rows as a single full-width cell,
  // which is exactly the shape an expanded detail panel needs.
  const tableRows = [];
  displayItems.forEach((item) => {
    tableRows.push(item);
    if (expandedRows[item.entity_id]) {
      tableRows.push({
        _isGroupHeader: true,
        id: `exp-${item.entity_id}`,
        content: prLoading[item.entity_id]
          ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading requisition…</div>
          : prCache[item.entity_id]
            ? (
              <PinnedDetail width={viewport.width}>
                <RequisitionExpandedDetail pr={prCache[item.entity_id]} />
              </PinnedDetail>
            )
            : <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Could not load this requisition.</div>,
      });
    }
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>{title || 'My Inbox'}</h2>
        
        <div style={{ display: 'flex', gap: 12 }}>
          {/* Pending Tab */}
          <button
            onClick={() => setActiveTab('PENDING')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: activeTab === 'PENDING' ? '#d97706' : '#fff',
              border: activeTab === 'PENDING' ? '1px solid #b45309' : '1px solid #d97706',
              color: activeTab === 'PENDING' ? '#fff' : '#d97706',
              padding: '6px 16px',
              borderRadius: 20,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
              opacity: activeTab === 'PENDING' ? 1 : 0.7
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
            Pending: <span style={{ fontWeight: 800 }}>{pendingItems.length}</span>
          </button>

          {/* Returned Tab */}
          <button
            onClick={() => setActiveTab('RETURNED')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: activeTab === 'RETURNED' ? '#ea580c' : '#fff',
              border: activeTab === 'RETURNED' ? '1px solid #c2410c' : '1px solid #ea580c',
              color: activeTab === 'RETURNED' ? '#fff' : '#ea580c',
              padding: '6px 16px',
              borderRadius: 20,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
              opacity: activeTab === 'RETURNED' ? 1 : 0.7,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 14 4 9 9 4"></polyline><path d="M20 20v-7a4 4 0 0 0-4-4H4"></path></svg>
            Returned: <span style={{ fontWeight: 800 }}>{returnedItems.length}</span>
          </button>

          {/* Rejected Tab */}
          <button
            onClick={() => setActiveTab('REJECTED')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: activeTab === 'REJECTED' ? '#ef4444' : '#fff',
              border: activeTab === 'REJECTED' ? '1px solid #b91c1c' : '1px solid #ef4444',
              color: activeTab === 'REJECTED' ? '#fff' : '#ef4444',
              padding: '6px 16px',
              borderRadius: 20,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
              opacity: activeTab === 'REJECTED' ? 1 : 0.7,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>
            Rejected: <span style={{ fontWeight: 800 }}>{rejectedItems.length}</span>
          </button>
        </div>
      </div>

      <Card style={{ border: '1px solid black', borderRadius: 8, boxShadow: 'none', overflow: 'hidden', padding: 0 }}>
        <div ref={viewport.ref}>
        {displayItems.length > 0 ? (
          <Table cols={tableCols} rows={tableRows} />
        ) : (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
            No {activeTab.toLowerCase()} requests! 🎉
          </div>
        )}
        </div>
      </Card>

      {showActionModal && selectedInstance && (
        <Modal 
          title={actionType === 'approve' ? 'Authorize Request' : (actionType === 'return' ? 'Return Request' : (actionType === 'delete' ? 'Delete Request' : 'Reject Request'))} 
          onClose={() => setShowActionModal(false)}
        >
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 13, marginBottom: 16 }}>
              You are about to <strong>{actionType}</strong> the {selectedInstance.entity_type.replace('_', ' ')} 
              <strong> {selectedInstance.context_data?.pr_number}</strong>.
            </p>

            {actionType !== 'delete' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Comment {(actionType === 'reject' || actionType === 'return') && <span style={{color: 'red'}}>*</span>}
                </label>
                <Input 
                  value={comment}
                  onChange={setComment}
                  placeholder={`Optional comment...`}
                  as="textarea"
                  rows={3}
                />
              </div>
            )}

            {actionType === 'return' && returnTargets.length > 1 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Return to <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                  title="Return To"
                  value={returnTarget}
                  onChange={setReturnTarget}
                  options={returnTargets.map((t) => ({
                    value: t.value,
                    label: t.is_default ? `${t.label} (default)` : t.label,
                  }))}
                />
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                  {returnTargets.find((t) => t.value === returnTarget)?.description
                    || 'Choose where this request goes back to.'}
                </div>
              </div>
            )}

            {actionType === 'return' && considerationStatuses.length > 0
              && offersConsiderationStatus(returnFromStep, returnTargets, returnTarget) && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Consideration Status <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                  title="Consideration Status"
                  value={considerationStatusId}
                  onChange={setConsiderationStatusId}
                  placeholder="Choose a heading"
                  options={considerationOptionsFor(considerationStatuses, returnFromStep).map((c) => ({
                    value: c.id,
                    label: `${c.icon ? c.icon + ' ' : ''}${c.name}`,
                  }))}
                />
              </div>
            )}

            {actionType === 'approve' && isFetchingApprovers && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading next step...</div>
            )}

            {actionType === 'approve' && !isFetchingApprovers && nextStepInfo && nextStepInfo.approver_mode !== 'is_final' && nextStepInfo.step_name && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  {nextStepInfo.approver_mode === 'returning_step'
                    ? `Goes back to - ${nextStepInfo.step_name}`
                    : `Next Approval Step - ${nextStepInfo.step_name}`}
                </label>
                {nextStepInfo.approver_mode === 'returning_step' ? (
                  // The returning approver is already recorded on the step, so
                  // there is nobody to nominate — just say where it's going.
                  <ReturnDestinationNote stepName={nextStepInfo.step_name} />
                ) : nextStepInfo.approver_mode === 'specific_user' ? (
                  <Select
                    value={selectedNextApprover}
                    onChange={setSelectedNextApprover}
                    options={nextStepInfo.eligible_users.map(a => ({ value: a.user_id || a.id, label: `${a.name} (${a.email})` }))}
                  />
                ) : (
                  <Select
                    value="step_queue"
                    onChange={() => {}}
                    options={[{ value: 'step_queue', label: `🏢 ${nextStepInfo.step_name}` }]}
                  />
                )}
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Btn variant="outline" disabled={acting} onClick={() => setShowActionModal(false)}>Cancel</Btn>
            <Btn 
              onClick={handleActionSubmit} 
              loading={acting}
              disabled={
                actionType === 'return'
                && offersConsiderationStatus(returnFromStep, returnTargets, returnTarget)
                && !considerationStatusId
              }
              style={{ background: actionType === 'approve' ? '#16a34a' : '#ef4444', color: '#fff', border: 'none' }}
            >
              {acting
                ? ACTION_PROGRESS[actionType] || 'Working…'
                : `Confirm ${actionType.charAt(0).toUpperCase() + actionType.slice(1)}`}
            </Btn>
          </div>
        </Modal>
      )}

      {/* ── Outcome Modal ──
          Driven by a lookup rather than a chain of `type === 'approve'`
          ternaries. The old version only knew 'approve' and 'reject', so a
          successful return or delete fell through every else-branch and was
          reported as a red "ACTION FAILED" — and, worse, was treated as a
          failure by the close handler, so the inbox never refreshed and the
          request appeared to still be sitting there. */}
      {outcomeModal && (() => {
        const OUTCOME_STYLE = {
          approve: { color: '#16a34a', bg: '#dcfce7', shadow: 'rgba(22,163,74,0.25)', heading: 'REQUEST APPROVED!', cta: 'OK / Continue', icon: <polyline points="20 6 9 17 4 12" /> },
          success: { color: '#16a34a', bg: '#dcfce7', shadow: 'rgba(22,163,74,0.25)', heading: 'DONE!', cta: 'Close', icon: <polyline points="20 6 9 17 4 12" /> },
          return: { color: '#f97316', bg: '#ffedd5', shadow: 'rgba(249,115,22,0.25)', heading: 'REQUEST RETURNED!', cta: 'Close', icon: (<><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></>) },
          reject: { color: '#ef4444', bg: '#fee2e2', shadow: 'rgba(239,68,68,0.25)', heading: 'REQUEST REJECTED!', cta: 'Close', icon: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>) },
          hold: { color: '#eab308', bg: '#fef9c3', shadow: 'rgba(234,179,8,0.25)', heading: 'REQUEST ON HOLD!', cta: 'Close', icon: (<><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></>) },
          error: { color: '#ef4444', bg: '#fee2e2', shadow: 'rgba(239,68,68,0.25)', heading: 'ACTION FAILED', cta: 'Close', icon: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>) },
        };
        const style = OUTCOME_STYLE[outcomeModal.type] || OUTCOME_STYLE.error;
        // Anything that is not an explicit error moved the request on, so the
        // inbox has to be re-read.
        const wasSuccess = outcomeModal.type !== 'error';
        const dismiss = () => { setOutcomeModal(null); if (wasSuccess) fetchInbox(); };
        return (
          <Modal title={outcomeModal.title} onClose={dismiss}>
            <div style={{ textAlign: 'center', padding: '20px 10px' }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                background: style.bg, border: `3px solid ${style.color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px auto', boxShadow: `0 0 24px ${style.shadow}`,
              }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={style.color}
                  strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  {style.icon}
                </svg>
              </div>

              <h3 style={{ fontSize: 20, fontWeight: 800, color: style.color, margin: '0 0 10px 0', letterSpacing: '-0.02em' }}>
                {style.heading}
              </h3>

              <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: '0 0 24px 0', maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
                {outcomeModal.message}
              </p>

              <button
                onClick={dismiss}
                style={{
                  background: style.color, color: '#ffffff', border: 'none',
                  padding: '12px 36px', borderRadius: 8, fontSize: 14, fontWeight: 700,
                  cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
              >
                {style.cta}
              </button>
            </div>
          </Modal>
        );
      })()}

    </div>
  );
}
